import type { Comment, FullWriterSplit } from 'types/track'

/**
 * Maps track metadata field keys to user-friendly display names.
 *
 * @param key - The metadata field key
 * @returns The display name for the field
 */
export const getTrackMetadataFieldName = (key: string): string => {
  switch (key) {
    case 'Client comments':
      return 'Client notes'
    case 'Internal comments':
      return 'Internal notes'
    case 'Comment':
      return 'Comments'
    case 'ProNumber':
      return 'PRO Number'
    case 'TrackNumber':
      return 'Order'
    default:
      return key
  }
}

/**
 * List of all available fields for track hover tooltips.
 */
export const availableTrackHoverFields = [
  'Title',
  'Artist',
  'Album',
  'Formats',
  'Composer',
  'Grouping',
  'Genre',
  'Year',
  'Release Date',
  'BPM',
  'Comments',
  'Internal comments',
  'Client comments',
  'Length',
  'Upload date',
  'Uploaded by',
  'Original filename',
] as const

/**
 * List of all available fields for the track details pane.
 */
export const availableTrackPaneFields = [
  'Composer',
  'Genre',
  'Grouping',
  'Year',
  'BPM',
  'Comments',
  'ISRC',
  'Internal comments',
  'Client comments',
  'Original filename',
] as const

/**
 * Default fields shown in track hover tooltips.
 */
export const defaultTrackHoverFields = [
  'Formats',
  'Composer',
  'Grouping',
  'Comments',
]

/**
 * Default fields shown in the track details pane.
 */
export const defaultTrackPaneFields = ['Composer', 'Genre', 'Grouping', 'Year']

/**
 * Formats a track comment for display, including user and visibility.
 *
 * @param param0 - Object containing comment, user, and visibility
 * @returns Formatted comment string
 */
export const getTrackCommentText = ({
  comment,
  user,
  visibility,
}: Comment): string => `${user.first_name} (${visibility}): ${comment}`

/**
 * Formats a writer split object into a display string.
 *
 * @param param0 - Object containing pro, publisher, split, and writer
 * @returns Formatted writer split string
 */
export const getWriterSplitText = ({
  pro,
  publisher,
  split,
  writer,
}: FullWriterSplit): string => {
  const writerName = writer.name || writer.email
  const writerSplit = split ? split + '%' : ''
  return [
    [writerName, writerSplit].join(' '),
    publisher ? publisher.name : false,
    pro ? pro.name : false,
  ]
    .filter(Boolean)
    .join(', ')
}
